import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from './auth';

export interface TourData {
  userID: number;
  isFirstLogin: boolean;
  tourCompleted: boolean;
}

export class TourManager {
  private static instance: TourManager;

  static getInstance(): TourManager {
    if (!TourManager.instance) {
      TourManager.instance = new TourManager();
    }
    return TourManager.instance;
  }

  /**
   * Prüft den Tour-Status für den aktuellen Benutzer
   */
  async checkTourStatus(): Promise<{ isFirstLogin: boolean; tourCompleted: boolean }> {
    try {
      console.log('🔍 Prüfe Tour-Status...');

      // Hole userID aus dem Access-Token
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (!accessToken) {
        console.log('❌ Kein Access-Token gefunden');
        return { isFirstLogin: false, tourCompleted: false };
      }

      let userID: number | null = null;
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        userID = payload.id; // Verwende 'id' statt 'userID'
        console.log('👤 UserID aus Token:', userID);
        console.log('👤 Vollständiger Token-Payload:', payload);
      } catch (error) {
        console.error('❌ Fehler beim Parsen des Access-Tokens:', error);
        return { isFirstLogin: false, tourCompleted: false };
      }

      if (!userID) {
        console.log('❌ Keine userID im Token gefunden');
        return { isFirstLogin: false, tourCompleted: false };
      }

      // Prüfe Tour-Status über API (ohne Authorization-Header)
      console.log('🔍 Rufe Tour-Status-Endpoint auf:', buildApiUrl(API_CONFIG.ENDPOINTS.TOUR_STATUS(userID)));
      
      // Verwende direkten fetch ohne Authorization-Header
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.TOUR_STATUS(userID)), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('📡 Tour-Status Response Status:', response.status);
      console.log('📡 Tour-Status Response Headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.status === 404) {
        // Keine Daten zurückgeliefert = erstes Login
        console.log('🎯 Erstes Login erkannt (404) - Tour wird benötigt');
        return { isFirstLogin: true, tourCompleted: false };
      } else if (response.ok) {
        const data = await response.json();
        console.log('📊 Tour-Status erhalten:', data);
        console.log('📊 Tour-Status Details:', {
          hasSeenTour: data.hasSeenTour,
          tourData: data.tourData,
          rawData: data
        });
        
        // API gibt hasSeenTour zurück - wenn false, dann ist es das erste Login
        const isFirstLogin = !data.hasSeenTour;
        const tourCompleted = data.hasSeenTour; // Wenn hasSeenTour true ist, dann ist die Tour abgeschlossen
        
        console.log('📊 Berechnete Werte:', {
          isFirstLogin,
          tourCompleted,
          hasSeenTour: data.hasSeenTour,
          tourData: data.tourData
        });
        
        return { 
          isFirstLogin, 
          tourCompleted 
        };
      } else {
        console.log('⚠️ Fehler beim Abrufen des Tour-Status:', response.status);
        console.log('⚠️ Response Text:', await response.text().catch(() => 'Could not read response text'));
        
        // Bei 401-Fehler: Tour nicht starten (unsicher ob DB-Eintrag existiert)
        if (response.status === 401) {
          console.log('❌ 401-Fehler - Tour wird NICHT gestartet (unsicher ob DB-Eintrag existiert)');
          return { isFirstLogin: false, tourCompleted: false };
        }
        
        // Bei 404 oder 500+ Fehlern: Tour als Fallback starten
        if (response.status === 404 || response.status >= 500) {
          console.log('🎯 API-Endpoint nicht verfügbar - starte Tour als Fallback');
          return { isFirstLogin: true, tourCompleted: false };
        }
        
        return { isFirstLogin: false, tourCompleted: false };
      }
    } catch (error) {
      console.error('❌ Fehler beim Tour-Status-Check:', error);
      console.log('⚠️ API-Fehler - Tour wird NICHT gestartet (unsicher ob DB-Eintrag existiert)');
      return { isFirstLogin: false, tourCompleted: false };
    }
  }

  /**
   * Markiert die Tour als abgeschlossen
   */
  async markTourCompleted(): Promise<boolean> {
    try {
      console.log('✅ Markiere Tour als abgeschlossen...');

      // Hole userID aus dem Access-Token
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (!accessToken) {
        console.log('❌ Kein Access-Token gefunden');
        return false;
      }

      let userID: number | null = null;
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        userID = payload.id; // Verwende 'id' statt 'userID'
        console.log('👤 UserID aus Token:', userID);
      } catch (error) {
        console.error('❌ Fehler beim Parsen des Access-Tokens:', error);
        return false;
      }

      if (!userID) {
        console.log('❌ Keine userID im Token gefunden');
        return false;
      }

      // Sende Tour-Abschluss an API
      console.log('📤 Sende Tour-Abschluss an API:', buildApiUrl(API_CONFIG.ENDPOINTS.TOUR_COMPLETED));
      console.log('📤 Request Body:', { userID });
      
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TOUR_COMPLETED), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userID })
      });

      console.log('📡 Tour-Abschluss Response Status:', response.status);
      console.log('📡 Tour-Abschluss Response Headers:', Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        console.log('✅ Tour erfolgreich als abgeschlossen markiert');
        return true;
      } else {
        console.error('❌ Fehler beim Markieren der Tour als abgeschlossen:', response.status);
        const errorText = await response.text().catch(() => 'Could not read response text');
        console.error('❌ Response Text:', errorText);
        return false;
      }
    } catch (error) {
      console.error('❌ Fehler beim Tour-Abschluss:', error);
      return false;
    }
  }

  /**
   * Prüft ob die Tour benötigt wird
   */
  async checkIfTourNeeded(): Promise<boolean> {
    try {
      console.log('🔍 Prüfe ob Tour benötigt wird...');
      const { isFirstLogin, tourCompleted } = await this.checkTourStatus();
      
      // Tour wird benötigt wenn es das erste Login ist UND die Tour noch nicht abgeschlossen wurde
      const needsTour = isFirstLogin && !tourCompleted;
      
      console.log('🎯 Tour benötigt:', needsTour);
      console.log('🎯 Details: isFirstLogin =', isFirstLogin, ', tourCompleted =', tourCompleted);
      console.log('🎯 Berechnung: isFirstLogin && !tourCompleted =', isFirstLogin, '&&', !tourCompleted, '=', needsTour);
      
      return needsTour;
    } catch (error) {
      console.error('❌ Fehler beim Tour-Check:', error);
      return false;
    }
  }
}

// Exportiere eine Singleton-Instanz
export const getTourManager = (): TourManager => {
  return TourManager.getInstance();
};
